import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AddPopover,
  AddSearch,
  AddList,
  AddItem,
  AddItemIconStep,
  AddItemInfo,
  AddItemName,
  AddItemDesc,
  AddEmpty,
} from '../../../components/chatflow/styles'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconMap: Record<string, any> = { step: AddItemIconStep }

/**
 * ConnectDropMenu — Floating popup that appears when the user releases
 * a source handle on the empty canvas.
 *
 * Positioned via `position: fixed` at the screen coordinates of the drop.
 * Reuses the styled-components from AddNodeMenu for visual consistency.
 *
 * Props:
 *  - screenX/screenY  → screen coordinates of the drop point
 *  - onSelect(type)   → called when a node type is chosen
 *  - onDismiss()      → called when clicking outside or pressing Escape
 */
interface ConnectDropMenuProps {
  screenX: number;
  screenY: number;
  onSelect: (type: string) => void;
  onDismiss: () => void;
}

export default function ConnectDropMenu({ screenX, screenY, onSelect, onDismiss }: ConnectDropMenuProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const menuRef  = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const NODE_TYPES = [
    {
      type: 'step',
      icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
      label: t('add_menu.step_label'),
      desc:  t('add_menu.step_desc'),
    },
  ]

  // Foca no input ao montar
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus()
  }, [])

  // Fecha ao clicar fora ou pressionar Escape
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onDismiss()
    }
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss() }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onDismiss])

  const filtered = NODE_TYPES.filter(
    (n) =>
      n.label.toLowerCase().includes(search.toLowerCase()) ||
      n.desc.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    /*
     * O AddPopover tem `position: absolute; left: 50%; transform: translateX(-50%)` embutido,
     * que causaria um "pulinho" ao renderizar. O wrapper abaixo neutraliza isso:
     * - position: relative with height 0 serves as an anchor for the absolute child
     * - paddingTop: 0 keeps the popover aligned to the top of the drop point
     */
    <div
      style={{
        position: 'fixed',
        left:     screenX + 12,
        top:      screenY,
        zIndex:   9999,
      }}
    >
      {/* Container que cancela o left:50% e translateX(-50%) do AddPopover */}
      <div
        ref={menuRef}
        style={{
          position:  'relative',
          transform: 'translateY(-50%)',
        }}
      >
        <AddPopover style={{ position: 'static', transform: 'none', left: 'auto', bottom: 'auto' }}>
          <AddSearch>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              placeholder={t('add_menu.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </AddSearch>

          <AddList>
            {filtered.map((n) => {
              const IconWrap = IconMap[n.type] || AddItemIconStep
              return (
                <AddItem
                  key={n.type}
                  onClick={() => { onSelect(n.type) }}
                >
                  <IconWrap>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={n.icon} />
                    </svg>
                  </IconWrap>
                  <AddItemInfo>
                    <AddItemName>{n.label}</AddItemName>
                    <AddItemDesc>{n.desc}</AddItemDesc>
                  </AddItemInfo>
                </AddItem>
              )
            })}
            {filtered.length === 0 && <AddEmpty>{t('add_menu.no_results')}</AddEmpty>}
          </AddList>
        </AddPopover>
      </div>
    </div>
  )
}
